import { Box, LinearProgress, Typography, useTheme } from "@mui/material";
import { FileText, Image } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SPACING } from "../../../common/constants/spacing";

const StepSidebar = ({ currentStep, setCurrentStep }) => {
  const { t } = useTranslation("assets");
  const theme = useTheme();

  const steps = [
    {
      id: 0,
      title: t("editModal.generalDetails"),
      icon: FileText,
      description: t("editModal.generalDetailsDescription"),
    },
    {
      id: 1,
      title: t("editModal.media"),
      icon: Image,
      description: t("editModal.mediaDescription"),
    },
  ];

  return (
    <Box
      sx={{
        width: "280px",
        backgroundColor: theme.palette.mode === "dark" ? "#0a0a0a" : "#F2F3F5",
        p: 3,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: SPACING.xl }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: SPACING.xs }}>
          {t("editModal.title")}
        </Typography>
        <Typography variant="bodySm" color="text.secondary">
          {t("editModal.subtitle")}
        </Typography>
      </Box>

      {/* Steps */}
      <Box sx={{ flex: 1 }}>
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const isActive = currentStep === index;
          const isCompleted = currentStep > index;

          return (
            <Box
              key={step.id}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                mb: SPACING.md,
              }}
              onClick={() => setCurrentStep(index)}
            >
              {/* Step Indicator */}
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: isActive ? "primary.main" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: SPACING.sm,
                  flexShrink: 0,
                }}
              >
                <StepIcon
                  size={16}
                  color={
                    isActive || isCompleted
                      ? "white"
                      : theme.palette.text.secondary
                  }
                />
              </Box>

              {/* Step Content */}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="bodyMd"
                  component="p"
                  fontWeight={isActive ? "bold" : "medium"}
                  color={isActive ? "primary.main" : "text.primary"}
                >
                  {step.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {step.description}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Progress */}
      <Box sx={{ mt: SPACING.sm }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: SPACING.xs, display: "block" }}
        >
          {t("editModal.stepProgress", {
            current: currentStep + 1,
            total: steps.length,
          })}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={((currentStep + 1) / steps.length) * 100}
          sx={{ height: 4, borderRadius: 2 }}
        />
      </Box>
    </Box>
  );
};

export default StepSidebar;
